import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { Moment } from 'moment';

interface DatepickerDayItemProps {
  isOtherMonth: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  isRangeStart?: boolean;
  isRangeEnd?: boolean;
  inRange?: boolean;
  day: Moment;
  isToday?: boolean;
  hasChanges?: boolean;
}

const getColor = (props: DatepickerDayItemProps) => {
  if (props.hasChanges) {
    return css`
      ${({ theme }) => theme.colors.differentBackground}
    `;
  }
  if (props.isSelected && props.isRangeEnd) {
    return css`
      ${({ theme }) => theme.colors.white}
    `;
  }

  if (props.isSelected && props.isRangeStart) {
    return css`
      ${({ theme }) => theme.colors.white}
    `;
  }

  if (props.isToday && props.isSelected && !props.inRange) {
    return css`
      ${({ theme }) => theme.colors.white}
    `;
  }

  if (
    props.isSelected &&
    props.inRange &&
    !props.isRangeEnd &&
    !props.isRangeStart
  ) {
    return css`
      ${({ theme }) => theme.colors.primary}
    `;
  }

  if (props.isSelected && !props.inRange) {
    return css`
      ${({ theme }) => theme.colors.white}
    `;
  }

  if (props.isToday && !props.isSelected && !props.isDisabled) {
    return css`
      ${({ theme }) => theme.colors.primary}
    `;
  }

  if (!props.isToday && props.inRange && !props.isDisabled) {
    return css`
      ${({ theme }) => theme.colors.primary}
    `;
  }

  if (props.isDisabled) {
    return css`
      ${({ theme }) => theme.colors.primary}
    `;
  }

  return css`
    ${({ theme }) => theme.colors.primary}
  `;
};

const getBGColor = (props: DatepickerDayItemProps) => {
  if (
    props.isSelected &&
    props.inRange &&
    !props.isRangeEnd &&
    !props.isRangeStart
  ) {
    return css`
      ${({ theme }) => rgba(theme.colors.blueColor, 0.1)}
    `;
  }

  if (props.isSelected || props.isRangeEnd || props.isRangeStart) {
    return css`
      ${({ theme }) => theme.colors.blueColor}
    `;
  }

  return 'transparent';
};

const getCursor = (props: DatepickerDayItemProps) => {
  if (props.isDisabled) {
    return 'auto';
  }

  return 'pointer';
};

const getBorderColor = (props: DatepickerDayItemProps) => {
  if (props.isToday && !props.isSelected) {
    return css`
      ${({ theme }) => theme.colors.secondary}
    `;
  }

  return 'transparent';
};

const getFontWeight = (props: DatepickerDayItemProps) => {
  if (props.isToday) {
    return '700';
  }

  if (
    props.isSelected &&
    props.inRange &&
    !props.isRangeEnd &&
    !props.isRangeStart
  ) {
    return '400';
  }

  return '400';
};

const isSingleSelection = (props: DatepickerDayItemProps) => {
  return !!(!props.inRange || props.isRangeEnd || props.isRangeStart);
};

const getBorderRadius = (props: DatepickerDayItemProps) => {
  if (isSingleSelection(props)) {
    return '16px';
  }

  if (props.isToday) {
    return '25%';
  }

  return '0';
};

export default styled.div.attrs<DatepickerDayItemProps>((props) => ({
  children: props.day.get('date'),
  title: props.day.format('LL'),
}))<DatepickerDayItemProps>`
  position: ${(props) => (props.inRange ? 'static' : 'relative')};
  line-height: 32px;
  font-weight: ${(props) => getFontWeight(props)};
  width: 100%;
  height: 32px;
  border-radius: ${(props) => getBorderRadius(props)};
  text-align: center;
  z-index: ${({ theme }) => theme.zIndices.normal};
  cursor: ${(props) => getCursor(props)};
  margin: 0 auto;
  color: ${(props) => getColor(props)};

  ${(props) =>
    props.isDisabled
      ? css`
          opacity: 0.15;
        `
      : null}

  &:after,
  &:before {
    z-index: ${({ theme }) => theme.zIndices.under};
    position: absolute;
  }

  &:after {
    content: '';
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${(props) => (isSingleSelection(props) ? '32px' : '100%')};
    height: ${(props) => (isSingleSelection(props) ? '32px' : '100%')};
    border-radius: ${(props) => getBorderRadius(props)};
    background-color: ${(props) => getBGColor(props)};
    border: 1px solid ${(props) => getBorderColor(props)};
    transition: background-color
      ${({ theme }) =>
        `${theme.transitions.duration} ${theme.transitions.type}`};
  }

  ${(props) =>
    props.isSelected &&
    props.inRange &&
    props.isRangeEnd &&
    !(props.isRangeStart && props.isRangeEnd)
      ? css`
          &:before {
            content: '';
            width: 50%;
            height: 100%;
            top: 50%;
            left: 0;
            right: 50%;
            transform: translate(0, -50%);
            background-color: ${({ theme }) => rgba(theme.colors.accent, 0.1)};
          }
        `
      : null};

  ${(props) =>
    props.isSelected &&
    props.inRange &&
    props.isRangeStart &&
    !(props.isRangeStart && props.isRangeEnd)
      ? css`
          &:before {
            content: '';
            width: 50%;
            height: 100%;
            top: 50%;
            left: 50%;
            right: 0;
            transform: translate(0, -50%);
            background-color: ${({ theme }) => rgba(theme.colors.accent, 0.1)};
          }
        `
      : null};

  ${(props) => (props.isOtherMonth ? `opacity: .5;` : null)};
`;
